import { useCallback, useEffect, useState } from 'react';
import useUserStore from '@/stores/UserStore';
import { Bell, Check, Circle } from 'lucide-react';
import { toast } from 'sonner';
import { useShallow } from 'zustand/react/shallow';

import { cn, formatRelativeTime } from '@/lib/utils';
import { fetchNotifications, markNotificationAsRead } from '@/hooks/user';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';

// Function to process HTML content and add target="_blank" to anchor tags
const processHtmlContent = (htmlContent) => {
  if (!htmlContent) return '';

  // Use a temporary div to parse and modify the HTML
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = htmlContent;

  // Find all anchor tags and add target="_blank" and rel="noopener noreferrer"
  const anchorTags = tempDiv.querySelectorAll('a');
  anchorTags.forEach((anchor) => {
    anchor.setAttribute('target', '_blank');
    anchor.setAttribute('rel', 'noopener noreferrer');
  });

  return tempDiv.innerHTML;
};

const NotificationsPopover = () => {
  const {
    notifications,
    setNotifications,
    updateIsNotificationsUnread,
    updateReadNotification,
    updateReadAllNotifications,
    isNotificationsUnread,
  } = useUserStore(
    useShallow((state) => ({
      notifications: state.notifications,
      setNotifications: state.setNotifications,
      updateIsNotificationsUnread: state.updateIsNotificationsUnread,
      updateReadNotification: state.updateReadNotification,
      updateReadAllNotifications: state.updateReadAllNotifications,
      isNotificationsUnread: state.isNotificationsUnread,
    }))
  );
  const [isOpen, setIsOpen] = useState(false);

  const handleMarkNotificationAsRead = async (
    action,
    notificationId = null
  ) => {
    try {
      await markNotificationAsRead(action, notificationId);
      if (action === 'mark_all_notifications') {
        updateReadAllNotifications();
        updateIsNotificationsUnread(false);
      } else if (action === 'mark_single_notification') {
        updateReadNotification(notificationId);
        // Check if all notifications are now read after marking this one
        const updatedNotifications = notifications.map((notification) =>
          notification._id === notificationId
            ? { ...notification, is_read: true }
            : notification
        );
        const allRead = updatedNotifications.every(
          (notification) => notification.is_read
        );
        updateIsNotificationsUnread(!allRead);
      }
    } catch (error) {
      toast.error(
        error.response?.data?.message || 'Error marking notification as read'
      );
    }
  };

  const fetchUserNotifications = useCallback(async () => {
    try {
      console.log('fetchUserNotifications is getting called');
      const response = await fetchNotifications();
      setNotifications(response.data);
      const hasUnreadNotifications = !response.is_all_read;
      updateIsNotificationsUnread(hasUnreadNotifications);

      // Auto-open popover if there are unread notifications with a delay
      if (hasUnreadNotifications) {
        setTimeout(() => {
          setIsOpen(true);
        }, 2500); // 2.5 seconds delay
      }
    } catch (error) {
      console.error('Error fetching user notifications:', error);
    }
  }, [setNotifications, updateIsNotificationsUnread]);

  useEffect(() => {
    fetchUserNotifications();
  }, [fetchUserNotifications]);

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <div
          className='relative inline-flex h-11 w-11 items-center justify-center bg-background text-gray-400 transition-colors hover:text-primary disabled:pointer-events-none'
          title='Notifications'
        >
          <Bell className='h-5 w-5' />
          {isNotificationsUnread && (
            <span className='absolute right-[13px] top-[13px] flex h-[8px] w-[8px] select-none items-center justify-center rounded-full bg-red-500'></span>
          )}
        </div>
      </PopoverTrigger>
      <PopoverContent
        className='h-screen w-[600px] p-0 shadow-2xl'
        align='end'
        side='right'
        sideOffset={10}
      >
        <div className='flex items-center justify-between p-4 pb-2'>
          <h4 className='font-semibold'>Notifications</h4>
          {isNotificationsUnread && (
            <Button
              size='sm'
              onClick={() =>
                handleMarkNotificationAsRead('mark_all_notifications')
              }
              className='h-auto py-1 text-xs'
            >
              Mark all read
            </Button>
          )}
        </div>
        <Separator />
        <ScrollArea className='h-[calc(100vh-55px)]'>
          <div className='p-1'>
            {!notifications || notifications.length === 0 ? (
              <div className='flex flex-col items-center justify-center py-8 text-center'>
                <Bell className='mb-2 h-8 w-8 text-muted-foreground' />
                <p className='text-sm text-muted-foreground'>
                  No notifications
                </p>
              </div>
            ) : (
              <div className='space-y-2 rounded-sm pr-2'>
                {notifications.map((notification) => (
                  <div
                    key={notification._id}
                    className={cn(
                      'group relative flex gap-3 rounded-lg border p-3 transition-colors hover:bg-accent',
                      !notification.is_read ? 'bg-accent/50' : ''
                    )}
                  >
                    <div className='min-w-0 flex-1'>
                      <h5
                        className={cn(
                          'text-sm font-semibold leading-relaxed',
                          !notification.is_read
                            ? 'font-bold text-foreground'
                            : 'text-muted-foreground'
                        )}
                      >
                        {notification.title}
                      </h5>
                      <div
                        className={cn(
                          'mt-1 whitespace-pre-line text-xs leading-relaxed [&_a]:text-primary [&_a]:underline [&_a]:hover:text-primary/80',
                          !notification.is_read
                            ? 'font-normal text-foreground'
                            : 'text-muted-foreground'
                        )}
                        dangerouslySetInnerHTML={{
                          __html: processHtmlContent(notification.message),
                        }}
                      />
                    </div>
                    <div className='flex flex-col items-end'>
                      <p className='text-xs text-muted-foreground'>
                        {formatRelativeTime(new Date(notification.created_at))}
                      </p>
                      <div className='flex items-center justify-center'>
                        <Button
                          variant='ghost'
                          size='sm'
                          onClick={() =>
                            !notification.is_read &&
                            handleMarkNotificationAsRead(
                              'mark_single_notification',
                              notification._id
                            )
                          }
                          className={cn(
                            'mt-2 h-6 w-6 p-0',
                            notification.is_read &&
                              'pointer-events-none opacity-50'
                          )}
                        >
                          {!notification.is_read && (
                            <div className='relative flex h-6 w-6 items-center justify-center'>
                              <div
                                className={cn(
                                  'mt-2 h-2 w-2 rounded-full transition-all duration-200',
                                  'bg-blue-600',
                                  'group-hover:hidden'
                                )}
                              />
                              <div className='absolute inset-0 flex items-center justify-center rounded-md opacity-0 transition-opacity group-hover:opacity-100'>
                                <div className='mt-2 flex h-5 w-5 items-center justify-center rounded-full border-2 border-blue-600 bg-blue-600'>
                                  <Check className='h-3 w-3 text-white' />
                                </div>
                              </div>
                            </div>
                          )}
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
};

export default NotificationsPopover;
